import {
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { DataLayerService } from 'src/app/shared/services/data-layer.service';
import { ErrorResponse } from 'src/app/shared/models/error.model';
import { User } from '@spartacus/user/account/root/model';
import { OffersPayload } from '../../models/offers-form.model';
import { MyOffersService } from '../../services/my-offers.service';
import { Offer, OfferConfirmationInfo } from '../../models/offers.model';

@Component({
  selector: 'app-offer',
  templateUrl: './offer.component.html',
  styleUrl: './offer.component.scss',
})
export class OfferComponent implements OnInit, OnDestroy {
  @Input({ required: true })
  offer!: Offer;

  @Input({ required: true })
  baseurl!: string;

  @Input({ required: true })
  user!: User;

  private readonly myOffersService = inject(MyOffersService);
  private readonly dataLayerService = inject(DataLayerService);

  private toastNotificationTimeout;
  showToastNotification = signal<boolean>(false);

  /**
   * NOTE: USONESHOP-223/240
   * Member variable necessary because after successfull submission
   * if we trigger a call to offers endpoint to get the updated offers
   * they are not immediately updated.
   * Needed to show the confirmation date and the accepted offer trait.
   * Once we reload the page the infos are get from the offer itself.
   * Might change in the future.
   */
  readonly offerConfirmationInfo = signal<OfferConfirmationInfo | null>(null);

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    const [firstTrait, secondTrait] = this.offer.offerTraits;
    if (this.offer.confirmedDate && (firstTrait.accepted || secondTrait.accepted)) {
      const acceptedOfferTraitName = firstTrait.accepted ? firstTrait.rebateName : secondTrait.rebateName;
      this.offerConfirmationInfo.set({
        confirmationDate: this.offer.confirmedDate,
        acceptedOfferTraitName: acceptedOfferTraitName
      });
    }
  }

  confirmOffer(offerPayload: OffersPayload): void {
    this.myOffersService
      .confirmOffer(offerPayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.offerConfirmationInfo.set({
              confirmationDate: res.acceptedDate,
              acceptedOfferTraitName: res.acceptedRebateName
            });

            this.dataLayerService.addtoDataLayer(
              'offerForm_FormEvent',
              {
                selected_offer: `${this.offer.name} & ${res.acceptedRebateName}`
              },
              null,
              null,
              'form_submit'
            );

            this.toggleToastNotification();
          }
        },
        error: (res: ErrorResponse) => {
          this.dataLayerService.addtoDataLayer(
            'offerForm_FormEvent',
            { error_type: res.errors[0].type },
            null,
            null,
            'form_error'
          );
        },
      });
  }

  closeToastNotification(): void {
    this.showToastNotification.set(false);
    clearTimeout(this.toastNotificationTimeout);
  }

  private toggleToastNotification(): void {
    this.showToastNotification.set(true);
    this.toastNotificationTimeout = setTimeout(() => {
      this.showToastNotification.set(false);
      clearTimeout(this.toastNotificationTimeout);
    }, 5000);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    clearTimeout(this.toastNotificationTimeout);
  }
}
